<template>
  <div class="renderPage cia-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="cia-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-office-building-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase cia-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Gestión de Compañías
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="cia-btn font-weight-bold" @click="incluir()">
        <v-icon left small>mdi-plus</v-icon> Nueva Compañía
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="cia-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar por nombre o cédula..."
          single-line hide-details outlined dense
          class="cia-search"
          style="max-width:420px"
        ></v-text-field>
        <v-spacer />
        <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="cia-table"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.cedula`]="{ item }">
          <span class="cia-code-badge">{{ item.cedula }}</span>
        </template>

        <template v-slot:[`item.nombre`]="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="font-weight-bold text-body-2">{{ item.nombre }}</span>
            <span class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">{{ item.nombreComercial }}</span>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark style="min-width:36px;height:36px;padding:0;border-radius:4px" @click.stop="editItem(item)">
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-office-building-marker-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron compañías registradas.</p>
            <v-btn color="primary" depressed small @click="getData()">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DRAWER FORMULARIO -->
    <v-navigation-drawer v-model="drawerForm" fixed right temporary width="700" class="cia-drawer">
      <div class="d-flex flex-column fill-height">

        <!-- HEADER DRAWER -->
        <div class="cia-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'cia-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">{{ editedIndex > -1 ? 'mdi-office-building-cog' : 'mdi-office-building-plus-outline' }}</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex > -1 ? 'Editar Compañía' : 'Nueva Compañía' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">{{ editedItem.cedula || 'SIN CÉDULA' }} · Información fiscal y configuración</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- FORMULARIO -->
        <div class="flex-grow-1 overflow-y-auto pa-5">
          <v-form ref="form" v-model="valid">

            <!-- DATOS GENERALES -->
            <div class="cia-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-information-outline</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Datos Generales</span>
            </div>
            <v-card flat class="cia-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-select
                    v-model="editedItem.tipoId"
                    :items="tiposIdentificacion"
                    item-text="descripcion"
                    item-value="tipoId"
                    label="Tipo Identificación"
                    outlined dense
                    prepend-inner-icon="mdi-card-account-mail-outline"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.cedula"
                    label="Identificación"
                    outlined dense
                    prepend-inner-icon="mdi-account-details-outline"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn color="primary" block depressed height="40" class="cia-btn font-weight-bold" @click="getClienteHacienda">
                    <v-icon left size="16">mdi-file-find</v-icon> Hacienda
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.nombre"
                    label="Nombre o Razón Social"
                    outlined dense
                    prepend-inner-icon="mdi-office-building-outline"
                    :rules="nameRules" required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.nombreComercial"
                    label="Nombre Comercial"
                    outlined dense
                    prepend-inner-icon="mdi-tag-outline"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="editedItem.moneda"
                    :items="monedas"
                    item-text="md_nombre"
                    item-value="md_codigo"
                    label="Moneda Principal"
                    outlined dense
                    prepend-inner-icon="mdi-cash"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="editedItem.moneda2"
                    :items="monedas"
                    item-text="md_nombre"
                    item-value="md_codigo"
                    label="Segunda Moneda"
                    outlined dense
                    prepend-inner-icon="mdi-cash-multiple"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="2" class="d-flex align-center">
                  <v-checkbox v-model="editedItem.ivaIncluido" label="IVA Incluido" color="primary" class="mt-1" hide-details></v-checkbox>
                </v-col>
                <v-col cols="12" sm="2" class="d-flex align-center">
                  <v-checkbox v-model="editedItem.esProd" label="Producción" color="success" class="mt-1" hide-details></v-checkbox>
                </v-col>
                <v-col cols="12" sm="3" class="d-flex align-center">
                  <v-checkbox v-model="editedItem.leerCorreoCompras" label="Leer Correo Compras" color="primary" class="mt-1" hide-details></v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.sucursal" label="Sucursal (default: 001)" outlined dense prepend-inner-icon="mdi-store-outline"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.terminal" label="Terminal (default: 00001)" outlined dense prepend-inner-icon="mdi-desktop-classic"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    v-model="modelFileImage"
                    label="Logo de la Empresa (JPG/JPEG)"
                    accept="image/jpg,image/jpeg"
                    prepend-icon=""
                    prepend-inner-icon="mdi-camera"
                    outlined dense
                    @change="onChangeCiaImage"
                    truncate-length="25"
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-card>

            <!-- EXPANSIÓN: UBICACIÓN -->
            <v-expansion-panels flat class="cia-panels mb-2" v-model="panels">
              <v-expansion-panel class="cia-expansion-panel mb-2">
                <v-expansion-panel-header class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px;min-height:48px">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-map-marker-radius-outline</v-icon>
                    <span :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Ubicación y Contacto</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="pt-2">
                    <v-col cols="6">
                      <v-autocomplete v-model="editedItem.provincia" :items="provincias" item-text="pr_nombre" item-value="pr_codigo" label="Provincia" outlined dense clearable @change="changeProvincia"></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete v-model="editedItem.canton" :items="cantones" item-text="ct_nombre" item-value="ct_codigo" label="Cantón" outlined dense clearable :disabled="!editedItem.provincia" @change="changeCantones"></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete v-model="editedItem.distrito" :items="distritos" item-text="dt_nombre" item-value="dt_codigo" label="Distrito" outlined dense clearable :disabled="!editedItem.canton" @change="changeDistritos"></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete v-model="editedItem.barrio" :items="barrios" item-text="br_nombre" item-value="br_codigo" label="Barrio" outlined dense clearable :disabled="!editedItem.distrito"></v-autocomplete>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field v-model="editedItem.areaTel" label="Área Tel." outlined dense type="number"></v-text-field>
                    </v-col>
                    <v-col cols="8">
                      <v-text-field v-model="editedItem.telefono" label="Teléfono" outlined dense type="number"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field v-model="editedItem.correo" label="Correo Electrónico" outlined dense prepend-inner-icon="mdi-email-outline"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="editedItem.direccion" label="Dirección Exacta" outlined dense rows="2"></v-textarea>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- EXPANSIÓN: HACIENDA -->
              <v-expansion-panel class="cia-expansion-panel mb-2">
                <v-expansion-panel-header class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px;min-height:48px">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-shield-check-outline</v-icon>
                    <span :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Conexión Hacienda</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="pt-2">
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="editedItem.usuariohacienda" label="Usuario Hacienda" outlined dense prepend-inner-icon="mdi-account-key-outline" autocomplete="off"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="editedItem.clavehacienda" label="Clave Hacienda" type="password" outlined dense prepend-inner-icon="mdi-lock-outline" autocomplete="new-password"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-file-input v-model="modelFile" label="Certificado (.p12)" accept=".p12" prepend-icon="" prepend-inner-icon="mdi-key-variant" outlined dense @change="onImageChange"></v-file-input>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="editedItem.claveCert" label="Contraseña Certificado" type="password" outlined dense prepend-inner-icon="mdi-lock-alert-outline" autocomplete="new-password"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="12">
                      <v-select
                        v-model="editedItem.codigoActividad"
                        :items="actividades"
                        item-text="descripcion"
                        item-value="codigo"
                        label="Actividad Económica por Defecto"
                        outlined dense
                        prepend-inner-icon="mdi-briefcase-outline"
                        clearable
                        hint="Se usará automáticamente al facturar si la empresa tiene una sola actividad o como selección predeterminada"
                        persistent-hint
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- EXPANSIÓN: CORREO -->
              <v-expansion-panel class="cia-expansion-panel mb-2">
                <v-expansion-panel-header class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px;min-height:48px">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-email-outline</v-icon>
                    <span :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Configuración de Correo</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="pt-2">
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="editedItem.claveCcorreo" label="Contraseña Correo" type="password" outlined dense prepend-inner-icon="mdi-lock-outline" autocomplete="new-password"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="editedItem.smtpCorreo" label="Servidor SMTP" outlined dense placeholder="smtp.gmail.com"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="editedItem.puertoCorreo" label="Puerto" outlined dense placeholder="587"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4" class="d-flex align-center">
                      <v-checkbox v-model="editedItem.usaSeguridad" label="Usar SSL/TLS" color="primary" class="mt-1" hide-details></v-checkbox>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- EXPANSIÓN: CONSECUTIVOS -->
              <v-expansion-panel class="cia-expansion-panel">
                <v-expansion-panel-header class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px;min-height:48px">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-counter</v-icon>
                    <span :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Consecutivos ({{ consecutivos.length }})</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense v-if="consecutivos.length > 0" class="cia-simple-table mt-2">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Tipo Documento</th>
                          <th class="text-right">Consecutivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="con in consecutivos" :key="con.tipoDocumento">
                          <td class="font-weight-medium">{{ getTipoDocNombre(con.tipoDocumento) }}</td>
                          <td class="text-right" style="width:160px">
                            <v-text-field
                              v-model.number="con.numConsecutivo"
                              outlined dense hide-details type="number"
                              style="max-width:140px;margin-left:auto"
                            ></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <div v-if="consecutivos.length > 0" class="d-flex justify-end mt-3">
                    <v-btn color="primary" depressed small class="cia-btn font-weight-bold" :loading="loadingConsecutivos" @click="saveConsecutivos()">
                      <v-icon left size="14">mdi-content-save-outline</v-icon> Guardar Consecutivos
                    </v-btn>
                  </div>
                  <div v-else class="text-center pa-4">
                    <p class="caption mb-2" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">
                      Sin consecutivos registrados para esta compañía.
                    </p>
                    <v-btn color="secondary" outlined small class="cia-btn font-weight-bold" @click="initConsecutivos()">
                      <v-icon left size="16">mdi-auto-fix</v-icon> Inicializar Consecutivos (01-08)
                    </v-btn>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

          </v-form>
        </div>

        <!-- FOOTER ACCIONES -->
        <div class="cia-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'cia-drawer-footer--dark' : ''">
          <v-btn v-if="editedIndex > -1" color="error" text class="cia-btn font-weight-bold" @click="deleteItem()">
            <v-icon left small>mdi-trash-can-outline</v-icon> Eliminar
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold cia-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold cia-btn" :loading="loading" @click="save()">
            {{ editedIndex > -1 ? 'GUARDAR' : 'CREAR COMPAÑÍA' }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  components: { Snackbar },
  data() {
    return {
      drawerForm: false,
      loading: false,
      valid: false,
      panels: undefined,
      search: "",
      snackText: "",
      snackValue: false,
      consecutivos: [],
      loadingConsecutivos: false,
      modelFile: null,
      modelFileImage: null,
      tempFile: "",
      tempFileImage: "",
      headers: [
        { text: "Cédula", value: "cedula", sortable: true },
        { text: "Nombre / Comercial", value: "nombre", sortable: true },
        { text: "Sucursal", value: "sucursal", sortable: false },
        { text: "Terminal", value: "terminal", sortable: false },
        { text: "Acciones", value: "actions", align: 'right', sortable: false },
      ],
      items: [],
      tiposIdentificacion: [],
      monedas: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      actividades: [],
      modeloActividades: { cia: "", actividades: [] },
      editedIndex: -1,
      editedItem: {
        nombre: "", nombreComercial: "", cedula: "",
        provincia: "", canton: "", distrito: "", barrio: "", direccion: "",
        sucursal: "", terminal: "", moneda: "", moneda2: "",
        usuariohacienda: "", clavehacienda: "", propietarioCert: "",
        certificado: "", claveCert: "", esProd: false,
        correo: "", claveCcorreo: "", smtpCorreo: "", puertoCorreo: "",
        usaSeguridad: false, areaTel: "", telefono: "", tipoId: "",
        codigoActividad: "", image: "", imagePath: "", ciaImage: "", ivaIncluido: false,
        leerCorreoCompras: true,
      },
      defaultItem: {
        nombre: "", nombreComercial: "", cedula: "",
        provincia: "", canton: "", distrito: "", barrio: "", direccion: "",
        sucursal: "", terminal: "", moneda: "", moneda2: "",
        usuariohacienda: "", clavehacienda: "", propietarioCert: "",
        certificado: "", claveCert: "", esProd: false,
        correo: "", claveCcorreo: "", smtpCorreo: "", puertoCorreo: "",
        usaSeguridad: false, areaTel: "506", telefono: "", tipoId: "",
        codigoActividad: "", image: "", imagePath: "", ciaImage: "", ivaIncluido: false,
        leerCorreoCompras: true,
      },
      nameRules: [(v) => !!v || "El nombre es requerido"],
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),
  },

  created() {
    this.getData();
    this.getTiposIdentificacion();
    this.getMonedas();
    this.getProvincias();
  },

  methods: {
    getData() {
      axios.get("api/Cias/getCias").then(r => { this.items = r.data; }).catch(e => console.error(e));
    },

    getTiposIdentificacion() {
      axios.get("api/General/getTiposIdentificacion").then(r => { this.tiposIdentificacion = r.data; });
    },

    getMonedas() {
      axios.get("api/General/getMonedas").then(r => { this.monedas = r.data; });
    },

    getProvincias() {
      axios.get("api/General/getProvincias").then(r => { this.provincias = r.data; });
    },

    getCantones() {
      axios.get("api/General/getCantones?provincia=" + this.editedItem.provincia).then(r => { this.cantones = r.data; });
    },

    getDistritos() {
      axios.get(`api/General/getDistritos?provincia=${this.editedItem.provincia}&canton=${this.editedItem.canton}`).then(r => { this.distritos = r.data; });
    },

    getBarrios() {
      axios.get(`api/General/getBarrios?provincia=${this.editedItem.provincia}&canton=${this.editedItem.canton}&distrito=${this.editedItem.distrito}`).then(r => { this.barrios = r.data; });
    },

    changeProvincia() { this.editedItem.canton = ""; this.editedItem.distrito = ""; this.editedItem.barrio = ""; this.getCantones(); },
    changeCantones() { this.editedItem.distrito = ""; this.editedItem.barrio = ""; this.getDistritos(); },
    changeDistritos() { this.editedItem.barrio = ""; this.getBarrios(); },

    getActividades() {
      if (!this.editedItem.cedula) return;
      axios.get("api/General/getActividadesByCia?cia=" + this.editedItem.cedula).then(r => { this.actividades = r.data; });
    },

    getClienteHacienda() {
      if (!this.editedItem.cedula) return;
      axios.get("https://api.hacienda.go.cr/fe/ae?identificacion=" + this.editedItem.cedula).then(r => {
        const d = r.data;
        this.editedItem.nombre = d.nombre || this.editedItem.nombre;
        this.editedItem.nombreComercial = d.nombre || this.editedItem.nombreComercial;
        if (d.tipoIdentificacion) this.editedItem.tipoId = d.tipoIdentificacion;
        if (d.situacion && d.situacion.estado === "Inscrito") {
          this.editedItem.esProd = true;
          this.editedItem.areaTel = "506";
        }
        if (d.actividades && d.actividades.length > 0) {
          this.actividades = d.actividades;
          if (this.actividades.length === 1) this.editedItem.codigoActividad = this.actividades[0].codigo;
        }
      }).catch(e => console.error(e));
    },

    getConsecutivos() {
      const { cedula, sucursal, terminal } = this.editedItem;
      if (!cedula) return;
      axios.get(`api/Cias/getConsecutivos?cia=${cedula}&sucursal=${sucursal}&terminal=${terminal}`)
        .then(r => { this.consecutivos = r.data; })
        .catch(e => console.error(e));
    },

    saveConsecutivos() {
      this.loadingConsecutivos = true;
      Promise.all(this.consecutivos.map(c => axios.post("api/Cias/updateConsecutivo", c)))
        .then(() => { this.snackText = "Consecutivos guardados correctamente."; this.snackValue = true; })
        .catch(e => { this.snackText = "Error al guardar los consecutivos."; this.snackValue = true; console.error(e); })
        .finally(() => { this.loadingConsecutivos = false; });
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.panels = undefined;
      this.changeProvincia();
      this.changeCantones();
      this.changeDistritos();
      this.getActividades();
      this.getConsecutivos();
      this.drawerForm = true;
    },

    incluir() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.consecutivos = [];
      this.actividades = [];
      this.panels = undefined;
      this.drawerForm = true;
    },

    cerrar() {
      this.drawerForm = false;
      this.consecutivos = [];
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.valid = false;
      });
    },

    save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      const me = this;
      const formData = new FormData();
      if (this.tempFile) formData.append("images", this.tempFile);
      if (this.tempFileImage) formData.append("images", this.tempFileImage);
      formData.append("ciaInfo", JSON.stringify(this.editedItem));

      const url = this.editedIndex > -1 ? "/api/Cias/updateCias" : "/api/Cias/insertCias";
      axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } })
        .then(() => {
          me.snackText = this.editedIndex > -1 ? "Compañía actualizada con éxito" : "Compañía registrada con éxito";
          me.snackValue = true;
          this.saveActividades();
          if (this.editedIndex === -1) {
            if (this.consecutivos.length > 0) this.saveConsecutivos();
          }
          me.getData();
          me.cerrar();
        })
        .catch(e => { console.error(e); me.snackText = "Error al guardar la compañía"; me.snackValue = true; })
        .finally(() => { me.loading = false; });
    },

    saveActividades() {
      this.modeloActividades.cia = this.editedItem.cedula;
      this.modeloActividades.actividades = this.actividades;
      axios.post("api/General/insertActividadesByCia", this.modeloActividades).catch(e => console.error(e));
    },

    deleteItem() {
      if (!confirm("¿Está seguro de que desea eliminar esta compañía?")) return;
      axios.delete("api/cias/deleteClientes?cia=" + this.editedItem.cedula)
        .then(() => { this.getData(); this.cerrar(); })
        .catch(e => console.error(e));
    },

    onImageChange(ev) { if (ev) { this.tempFile = ev; this.editedItem.certificado = ev.name; } },
    onChangeCiaImage(ev) { if (ev) { this.tempFileImage = ev; this.editedItem.imagePath = ev.name; } },

    getTipoDocNombre(tipo) {
      const n = { '01': 'Factura Electrónica', '02': 'Nota de Débito', '03': 'Nota de Crédito', '04': 'Tiquete Electrónico', '05': 'Confirmación Comprobante', '06': 'Confirmación Parcial', '07': 'Aceptación / Rechazo', '08': 'Factura de Compra' };
      return n[tipo] || `Tipo ${tipo}`;
    },

    initConsecutivos() {
      const tipos = ['01', '02', '03', '04', '05', '06', '07', '08'];
      this.consecutivos = tipos.map(t => ({
        cia: this.editedItem.cedula,
        sucursal: this.editedItem.sucursal || '001',
        terminal: this.editedItem.terminal || '00001',
        tipoDocumento: t,
        numConsecutivo: 1
      }));
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.cia-container {
  background-color: #f1f5f9;
  min-height: 100vh;
}
.theme--dark.cia-container {
  background-color: #0f172a;
}

/* ===== TOOLBAR ===== */
.cia-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.theme--dark .cia-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.cia-toolbar-title {
  font-size: 0.95rem !important;
  letter-spacing: 1.5px;
}

/* ===== BUTTONS ===== */
.cia-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}

/* ===== TABLE ===== */
.cia-table-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.cia-table { background: transparent !important; }
.cia-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.cia-table >>> tbody tr { cursor: pointer; }
.cia-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.05) !important;
}
.theme--dark .cia-table >>> tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* ===== CODE BADGE ===== */
.cia-code-badge {
  font-family: monospace;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 2px 6px;
  background: rgba(55, 71, 79, 0.08);
  border-radius: 3px;
  color: #37474f;
}
.theme--dark .cia-code-badge {
  background: rgba(255, 255, 255, 0.1);
  color: #b0bec5;
}

/* ===== SEARCH ===== */
.cia-search >>> .v-input__slot {
  border-radius: 4px !important;
}

/* ===== DRAWER ===== */
.cia-drawer >>> .v-navigation-drawer__content {
  background-color: #ffffff !important;
}
.theme--dark .cia-drawer >>> .v-navigation-drawer__content {
  background-color: #0f172a !important;
}

/* ===== DRAWER HEADER ===== */
.cia-drawer-header {
  background-color: #37474f;
  flex-shrink: 0;
}
.cia-drawer-header--dark {
  background-color: #263238 !important;
}

/* ===== DRAWER FOOTER ===== */
.cia-drawer-footer {
  background-color: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.cia-drawer-footer--dark {
  background-color: #1e293b !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07) !important;
}

/* ===== SECTION LABEL ===== */
.cia-section-label {
  display: flex;
  align-items: center;
}

/* ===== SECTION CARD ===== */
.cia-section-card {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  background-color: #ffffff !important;
}
.theme--dark .cia-section-card {
  border-color: rgba(255, 255, 255, 0.07) !important;
  background-color: #1e293b !important;
}

/* ===== EXPANSION PANELS ===== */
.cia-panels {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  overflow: hidden;
}
.theme--dark .cia-panels {
  border-color: rgba(255, 255, 255, 0.07) !important;
}
.cia-expansion-panel {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.cia-expansion-panel:last-child {
  border-bottom: none !important;
}
.theme--dark .cia-expansion-panel {
  border-bottom-color: rgba(255, 255, 255, 0.05) !important;
}
.cia-panels >>> .v-expansion-panel {
  background-color: #ffffff !important;
}
.theme--dark .cia-panels >>> .v-expansion-panel {
  background-color: #1e293b !important;
}

/* ===== SIMPLE TABLE ===== */
.cia-simple-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
}
</style>